import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Zap, Crown, Building2, Check, ArrowRight, X, Send, Users, Clock, Brain, BookOpen, Award, Shield, MessageSquare, Star, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PricingPage() {
  const { user, getAuthHeaders } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [loadingPaypal, setLoadingPaypal] = useState(null);
  const [showEnterpriseForm, setShowEnterpriseForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', company: '', employees_count: '', message: '' });
  const [formSent, setFormSent] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const currentPlan = user?.plan || 'none';

  const handleCheckout = async (planId) => {
    if (!user) { navigate('/auth?mode=register'); return; }
    setLoadingPlan(planId);
    try {
      const res = await fetch(`${API}/api/checkout/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ plan_id: planId, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (err) { console.error(err); }
    setLoadingPlan(null);
  };

  const handlePayPalCheckout = async (planId) => {
    if (!user) { navigate('/auth?mode=register'); return; }
    setLoadingPaypal(planId);
    try {
      const res = await fetch(`${API}/api/paypal/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ plan_id: planId, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (data.approve_url) window.location.href = data.approve_url;
    } catch (err) { console.error(err); }
    setLoadingPaypal(null);
  };

  const handleEnterpriseSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await fetch(`${API}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      setFormSent(true);
    } catch (err) { console.error(err); }
    setFormLoading(false);
  };

  const plans = [
    {
      id: 'decouverte',
      name: 'IA Starter',
      subtitle: language === 'fr' ? 'Débuter avec l\'IA' : 'Get started with AI',
      price: '0',
      billing: language === 'fr' ? 'gratuit' : 'free',
      billingDetail: language === 'fr' ? 'Valable 1 semaine' : 'Valid for 1 week',
      icon: Sparkles,
      color: 'border-gray-200',
      btnColor: 'bg-brand-dark text-white hover:bg-gray-800',
      features: language === 'fr'
        ? ['1 heure de formation express (4 modules)', '3 outils IA gratuits que personne n\'utilise', '5 erreurs avec ChatGPT', 'Ecrire un bon prompt', 'Exercice pratique', '1 quiz inclus', 'Assistant IA inclus', 'Valable 1 semaine']
        : ['1 hour express training (4 modules)', '3 free AI tools nobody uses', '5 ChatGPT mistakes', 'Writing a good prompt', 'Practical exercise', '1 quiz included', 'AI Assistant included', 'Valid for 1 week'],
      quizLabel: language === 'fr' ? '1 quiz' : '1 quiz',
      aiLabel: language === 'fr' ? 'Oui (7 jours)' : 'Yes (7 days)',
    },
    {
      id: 'essentiel',
      name: 'IA Fondations',
      subtitle: language === 'fr' ? 'Automatiser avec l\'IA' : 'Automate with AI',
      price: '19',
      billing: language === 'fr' ? '/ mois' : '/ month',
      billingDetail: language === 'fr' ? 'Abonnement mensuel' : 'Monthly subscription',
      icon: Zap,
      color: 'border-blue-200',
      btnColor: 'bg-brand-blue text-white hover:brightness-110',
      features: language === 'fr'
        ? ['Programmes complets 2h + 5h (11 modules)', 'Prompt Engineering avancé', 'IA pour la productivité', 'RAG et outils IA nouvelle génération', 'Quiz adaptatifs avec corrections', 'Lexique IA complet (15+ outils)', 'Progression sauvegardée', 'Evaluations']
        : ['Complete programs 2h + 5h (11 modules)', 'Advanced Prompt Engineering', 'AI for Productivity', 'RAG & next-gen AI tools', 'Adaptive quizzes with corrections', 'Complete AI Lexicon (15+ tools)', 'Saved progress', 'Evaluations'],
      quizLabel: language === 'fr' ? '4 quiz adaptatifs' : '4 adaptive quizzes',
      aiLabel: language === 'fr' ? 'Non' : 'No',
    },
    {
      id: 'pro',
      name: 'IA Pro',
      subtitle: language === 'fr' ? 'Gagner du temps' : 'Save time',
      price: '49',
      billing: language === 'fr' ? '/ mois' : '/ month',
      billingDetail: language === 'fr' ? 'Abonnement mensuel' : 'Monthly subscription',
      icon: Crown,
      popular: true,
      color: 'border-orange-300 shadow-xl shadow-orange-100/50',
      btnColor: 'bg-brand-orange text-white hover:brightness-110',
      features: language === 'fr'
        ? ['Formation complète 8h (10 modules)', 'Assistant IA illimité (Claude)', 'Agents IA autonomes pour l\'entreprise', 'Contenu vidéos intégrés dans les cours', 'Certificats téléchargeables', 'Lexique IA complet (15+ outils)', 'Système de badges et streaks', 'Support prioritaire']
        : ['Complete 8h training (10 modules)', 'Unlimited AI Assistant (Claude)', 'Autonomous AI agents for business', 'Video content integrated in courses', 'Downloadable certificates', 'Complete AI Lexicon (15+ tools)', 'Badges and streaks system', 'Priority support'],
      quizLabel: language === 'fr' ? '7 quiz + mode adaptatif' : '7 quizzes + adaptive mode',
      aiLabel: language === 'fr' ? 'Illimité (Claude)' : 'Unlimited (Claude)',
    },
  ];

  const checkColor = (id) => {
    if (id === 'decouverte') return 'text-gray-400';
    if (id === 'essentiel') return 'text-brand-blue';
    return 'text-brand-orange';
  };

  const iconBg = (id) => {
    if (id === 'decouverte') return 'bg-gray-100 dark:bg-gray-800';
    if (id === 'essentiel') return 'bg-blue-50 dark:bg-blue-900/30';
    return 'bg-orange-50 dark:bg-orange-900/30';
  };

  const iconColor = (id) => {
    if (id === 'decouverte') return 'text-gray-500';
    if (id === 'essentiel') return 'text-brand-blue';
    return 'text-brand-orange';
  };

  const planOrder = ['none', 'decouverte', 'essentiel', 'pro', 'entreprise'];
  const isUpgrade = (planId) => planOrder.indexOf(planId) > planOrder.indexOf(currentPlan);

  return (
    <div className="min-h-screen bg-[#E8ECF1] dark:bg-[#0f1117] transition-colors duration-300" data-testid="pricing-page">
      <SEO
        title={language === 'fr' ? 'Tarifs Formation IA - IA Academy' : 'AI Training Pricing - IA Academy'}
        description={language === 'fr'
          ? "Choisissez votre plan IA Academy : IA Starter gratuit, IA Fondations (19€/mois), IA Pro (49€/mois) ou IA Expert (99€/personne/mois). Formation IA complète avec assistant Claude."
          : "Choose your IA Academy plan: Free IA Starter, IA Fondations (€19/mo), IA Pro (€49/mo) or IA Expert (€99/person/mo). Complete AI training with Claude assistant."}
        path="/pricing"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "Quelle est la différence entre IA Fondations et IA Pro ?", "acceptedAnswer": { "@type": "Answer", "text": "Le plan IA Fondations (19€/mois) donne accès aux programmes 2h et 5h avec quiz adaptatifs et lexique IA. IA Pro (49€/mois) ajoute tous les modules, l'assistant IA Claude illimité, les certificats et le support prioritaire." }},
            { "@type": "Question", "name": "Comment fonctionne le plan IA Starter ?", "acceptedAnswer": { "@type": "Answer", "text": "Le plan IA Starter est gratuit et offre 1 heure de formation express avec assistant IA inclus, valable 1 semaine. Idéal pour tester la plateforme." }},
            { "@type": "Question", "name": "Comment fonctionne le plan IA Expert ?", "acceptedAnswer": { "@type": "Answer", "text": "Le plan IA Expert est à 99€ par personne par mois. Il inclut 13 modules exclusifs couvrant la stratégie IA, l'AI Act, la gouvernance des données, la conduite du changement, les agents IA autonomes, un tableau de bord manager et un support dédié. Contact : contact@ia-academy.ai" }},
            { "@type": "Question", "name": "Puis-je changer de plan ?", "acceptedAnswer": { "@type": "Answer", "text": "Oui, vous pouvez passer au plan supérieur à tout moment. Votre progression est conservée." }},
            { "@type": "Question", "name": "Puis-je obtenir un remboursement ?", "acceptedAnswer": { "@type": "Answer", "text": "Oui, vous disposez de 7 jours pour demander un remboursement." }}
          ]
        }}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <img src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png" alt="IA Academy" className="w-14 h-14 rounded-xl mx-auto mb-4 shadow-md" data-testid="pricing-logo" />
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white tracking-tight" style={{ fontFamily: 'Poppins' }} data-testid="pricing-title">
            {t('pricing_title')}
          </h1>
          <p className="text-xs text-slate-400 dark:text-slate-500 tracking-wider font-medium mt-1">
            {language === 'fr' ? 'IA Academy — Progressez. 30 min/jour. Résultats concrets.' : 'IA Academy — Level Up. 30 Min a Day. Real Results.'}
          </p>
          <p className="text-base md:text-lg text-slate-500 dark:text-slate-400 mt-3 max-w-xl mx-auto">
            {language === 'fr'
              ? 'Investissez dans votre avenir. Des abonnements mensuels accessibles pour une formation premium.'
              : 'Invest in your future. Affordable monthly subscriptions for premium training.'}
          </p>
        </div>

        {/* Pricing Grid - 4 columns */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 items-start">
          {plans.map((plan) => {
            const isCurrentPlan = currentPlan === plan.id;
            const Icon = plan.icon;

            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl border-2 p-6 transition-all hover:shadow-lg ${plan.color} ${
                  plan.popular ? 'lg:-mt-4 lg:mb-0 bg-gradient-to-b from-orange-50/40 to-white dark:from-orange-900/10 dark:to-[#1a1d27]' : 'bg-white dark:bg-[#1a1d27]'
                } dark:border-gray-700`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.popular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-orange text-white border-0 px-3 py-1 shadow-md" data-testid="popular-badge">
                    {t('pricing_popular')}
                  </Badge>
                )}

                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconBg(plan.id)}`}>
                    <Icon className={`w-5 h-5 ${iconColor(plan.id)}`} />
                  </div>
                  <h2 className="text-lg font-bold text-brand-dark dark:text-white" style={{ fontFamily: 'Poppins' }}>
                    {plan.name}
                  </h2>
                </div>
                <p className="text-xs text-slate-400 dark:text-slate-500 mb-4 ml-12">{plan.subtitle}</p>

                <div className="mb-1">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Poppins' }}>
                      {plan.price}€
                    </span>
                    {plan.billing && (
                      <span className="text-sm text-slate-400 dark:text-slate-500">{plan.billing}</span>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-400 dark:text-slate-500 mb-5">{plan.billingDetail}</p>

                {/* Quiz & AI badges */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                    plan.id === 'decouverte' ? 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400' :
                    plan.id === 'essentiel' ? 'bg-blue-50 dark:bg-blue-900/30 text-brand-blue' :
                    'bg-orange-50 dark:bg-orange-900/30 text-brand-orange'
                  }`}>
                    <BookOpen className="w-3 h-3" /> {plan.quizLabel}
                  </span>
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                    plan.aiLabel === 'Non' ? 'bg-gray-100 dark:bg-gray-800 text-gray-400' : 'bg-blue-50 dark:bg-blue-900/30 text-brand-blue'
                  }`}>
                    <MessageSquare className="w-3 h-3" /> {language === 'fr' ? 'IA' : 'AI'}: {plan.aiLabel}
                  </span>
                </div>

                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((feat, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${checkColor(plan.id)}`} />
                      {feat}
                    </li>
                  ))}
                </ul>

                {isCurrentPlan ? (
                  <Button className="w-full bg-blue-50 dark:bg-blue-900/30 text-brand-blue border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/50 cursor-default" disabled data-testid={`plan-current-${plan.id}`}>
                    <Check className="w-4 h-4 mr-2" /> {t('pricing_current')}
                  </Button>
                ) : plan.id === 'decouverte' ? (
                  <Button variant="outline" className={`w-full ${plan.btnColor}`} onClick={() => navigate(user ? '/dashboard' : '/auth?mode=register')} data-testid={`plan-btn-${plan.id}`}>
                    {user ? (language === 'fr' ? 'Accéder au Dashboard' : 'Go to Dashboard') : (language === 'fr' ? 'Essayer gratuitement' : 'Try for free')}
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <Button className={`w-full active:scale-[0.98] transition-transform ${plan.btnColor}`} onClick={() => handleCheckout(plan.id)} disabled={loadingPlan === plan.id} data-testid={`plan-btn-${plan.id}`}>
                      {loadingPlan === plan.id ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>{isUpgrade(plan.id) ? t('pricing_upgrade') : (language === 'fr' ? 'Carte bancaire' : 'Credit card')} <ArrowRight className="w-4 h-4 ml-2" /></>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full border-[#0070ba] text-[#0070ba] hover:bg-[#0070ba]/10 dark:border-[#0070ba] dark:text-[#0070ba] dark:hover:bg-[#0070ba]/20 active:scale-[0.98] transition-transform text-sm"
                      onClick={() => handlePayPalCheckout(plan.id)}
                      disabled={loadingPaypal === plan.id}
                      data-testid={`plan-paypal-${plan.id}`}
                    >
                      {loadingPaypal === plan.id ? (
                        <div className="w-4 h-4 border-2 border-[#0070ba] border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>{language === 'fr' ? 'Payer avec' : 'Pay with'} <span className="font-bold ml-1">Pay</span><span className="font-bold text-[#003087]">Pal</span></>
                      )}
                    </Button>
                  </div>
                )}

                {isCurrentPlan && user?.subscription?.expires_at && (
                  <p className="text-xs text-slate-400 dark:text-slate-500 text-center mt-3">
                    {language === 'fr' ? 'Expire le' : 'Expires on'}{' '}
                    {new Date(user.subscription.expires_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                )}
              </div>
            );
          })}

          {/* Enterprise Plan */}
          <div className="relative bg-brand-dark rounded-2xl border border-brand-orange/25 p-6 text-white shadow-xl" data-testid="plan-card-enterprise">
            <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-orange text-white border-0 px-3 py-1 font-semibold shadow-md" data-testid="enterprise-badge">
              {language === 'fr' ? 'Formation complète' : 'Full training'}
            </Badge>

            <div className="flex items-center gap-2 mb-1">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-brand-orange/15 border border-brand-orange/20">
                <Building2 className="w-5 h-5 text-brand-orange" />
              </div>
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Poppins' }}>
                {language === 'fr' ? 'IA Expert' : 'IA Expert'}
              </h2>
            </div>
            <p className="text-xs text-brand-orange/60 mb-4 ml-12">{language === 'fr' ? 'Générer du temps et des revenus' : 'Generate time and revenue'}</p>

            <div className="mb-1">
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold text-white" style={{ fontFamily: 'Poppins' }}>99€</span>
                <span className="text-sm text-slate-400">/ {language === 'fr' ? 'personne / mois' : 'person / month'}</span>
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-5">{language === 'fr' ? 'Facturation entreprise' : 'Business invoicing'}</p>

            {/* Quiz & AI badges */}
            <div className="flex flex-wrap gap-1.5 mb-4">
              <span className="inline-flex items-center gap-1 text-xs bg-brand-orange/10 text-brand-orange px-2 py-0.5 rounded-full border border-brand-orange/20">
                <BookOpen className="w-3 h-3" /> {language === 'fr' ? '9 quiz + vidéos' : '9 quizzes + videos'}
              </span>
              <span className="inline-flex items-center gap-1 text-xs bg-brand-blue/10 text-blue-400 px-2 py-0.5 rounded-full border border-brand-blue/20">
                <MessageSquare className="w-3 h-3" /> {language === 'fr' ? 'IA illimité' : 'Unlimited AI'}
              </span>
            </div>

            <ul className="space-y-2.5 mb-6">
              {(language === 'fr'
                ? [
                  'Tous les modules (13 modules)',
                  'Stratégie IA et ROI en Entreprise',
                  'AI Act, Gouvernance et Protection des Données',
                  'Conduite du Changement et Déploiement IA',
                  'Agents IA Autonomes',
                  'CRM et Ventes augmentés par l\'IA',
                  'Analyse de Données et IA',
                  'Assistant IA illimité',
                  'Certificats personnalisés',
                  'Tableau de bord manager',
                  'Support dédié prioritaire',
                  'Contact : contact@ia-academy.ai',
                ]
                : [
                  'All modules (13 modules)',
                  'AI Strategy and ROI for Business',
                  'AI Act, Governance & Data Protection',
                  'AI Change Management & Deployment',
                  'Autonomous AI Agents',
                  'AI-Powered CRM & Sales',
                  'Data Analysis & AI',
                  'Unlimited AI Assistant',
                  'Custom certificates',
                  'Manager dashboard',
                  'Dedicated priority support',
                  'Contact: contact@ia-academy.ai',
                ]
              ).map((feat, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                  {feat.startsWith('Contact') ? (
                    <>
                      <Mail className="w-4 h-4 mt-0.5 shrink-0 text-brand-orange" />
                      <a href="mailto:contact@ia-academy.ai" className="text-brand-orange hover:text-orange-300 underline underline-offset-2 transition-colors" data-testid="expert-contact-email">
                        contact@ia-academy.ai
                      </a>
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mt-0.5 shrink-0 text-brand-orange" />
                      {feat}
                    </>
                  )}
                </li>
              ))}
            </ul>

            {currentPlan === 'entreprise' ? (
              <Button className="w-full bg-brand-orange/15 text-brand-orange border border-brand-orange/30 cursor-default" disabled data-testid="plan-current-entreprise">
                <Check className="w-4 h-4 mr-2" /> {t('pricing_current')}
              </Button>
            ) : (
              <div className="space-y-2">
                <Button
                  className="w-full bg-brand-orange text-white hover:brightness-110 font-semibold active:scale-[0.98] transition-all shadow-md shadow-brand-orange/20"
                  onClick={() => handleCheckout('entreprise')}
                  disabled={loadingPlan === 'entreprise'}
                  data-testid="enterprise-checkout-btn"
                >
                  {loadingPlan === 'entreprise' ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>{language === 'fr' ? 'Carte bancaire' : 'Credit card'} <ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-[#0070ba]/50 text-[#0070ba] hover:bg-[#0070ba]/10 active:scale-[0.98] transition-transform text-sm"
                  onClick={() => handlePayPalCheckout('entreprise')}
                  disabled={loadingPaypal === 'entreprise'}
                  data-testid="enterprise-paypal-btn"
                >
                  {loadingPaypal === 'entreprise' ? (
                    <div className="w-4 h-4 border-2 border-[#0070ba] border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>{language === 'fr' ? 'Payer avec' : 'Pay with'} <span className="font-bold ml-1">Pay</span><span className="font-bold text-[#003087]">Pal</span></>
                  )}
                </Button>
                <a
                  href="mailto:contact@ia-academy.ai?subject=Demande%20de%20devis%20IA%20Expert"
                  className="w-full text-center text-xs text-brand-orange/60 hover:text-brand-orange transition-colors py-1 block"
                  data-testid="enterprise-contact-btn"
                >
                  <Send className="w-3 h-3 inline mr-1" />
                  {language === 'fr' ? 'Demander un devis sur mesure' : 'Request a custom quote'}
                </a>
              </div>
            )}

            {currentPlan === 'entreprise' && user?.subscription?.expires_at && (
              <p className="text-xs text-slate-500 text-center mt-3">
                {language === 'fr' ? 'Expire le' : 'Expires on'}{' '}
                {new Date(user.subscription.expires_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            )}
          </div>
        </div>

        {/* Testimonials */}
        <div className="mt-16" data-testid="pricing-testimonials">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white text-center mb-2" style={{ fontFamily: 'Poppins' }}>
            {language === 'fr' ? 'Ils nous font confiance' : 'They trust us'}
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 text-center mb-8">
            {language === 'fr' ? 'Découvrez les retours de nos apprenants' : 'See feedback from our learners'}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {(language === 'fr' ? [
              { name: 'Lucas M.', role: 'Lycéen, Terminale', avatar: 'LM', quote: 'J\'ai compris en 2h ce que mes profs n\'ont jamais pu m\'expliquer sur l\'IA. Les quiz m\'ont aidé à valider mes acquis pour Parcoursup.', color: 'bg-brand-blue' },
              { name: 'Sarah K.', role: 'Chef de projet digital', avatar: 'SK', quote: 'Le Playground IA m\'a permis de tester des prompts concrets pour mon travail. J\'ai gagné 2h par jour sur mes tâches répétitives.', color: 'bg-brand-orange' },
              { name: 'Philippe D.', role: 'DG, PME industrielle', avatar: 'PD', quote: 'Le module Stratégie IA m\'a donné les arguments pour convaincre mon COMEX. On a lancé notre premier projet IA en 3 mois.', color: 'bg-brand-dark' },
              { name: 'Amina R.', role: 'En reconversion', avatar: 'AR', quote: 'Débutante totale, j\'ai pu suivre sans difficulté. Le parcours personnalisé m\'a guidé vers le bon programme. Très bien fait !', color: 'bg-stone-600' },
            ] : [
              { name: 'Lucas M.', role: 'High school student', avatar: 'LM', quote: 'I understood in 2 hours what my teachers could never explain about AI. The quizzes helped me validate my knowledge.', color: 'bg-brand-blue' },
              { name: 'Sarah K.', role: 'Digital project manager', avatar: 'SK', quote: 'The AI Playground let me test concrete prompts for my work. I save 2 hours a day on repetitive tasks.', color: 'bg-brand-orange' },
              { name: 'Philippe D.', role: 'CEO, Industrial SME', avatar: 'PD', quote: 'The AI Strategy module gave me the arguments to convince my board. We launched our first AI project in 3 months.', color: 'bg-brand-dark' },
              { name: 'Amina R.', role: 'Career changer', avatar: 'AR', quote: 'Complete beginner, I was able to follow easily. The personalized path guided me to the right program. Very well done!', color: 'bg-stone-600' },
            ]).map((t, i) => (
              <div key={i} className="bg-white dark:bg-[#1a1d27] rounded-xl border border-slate-200 dark:border-gray-700 p-5 hover:shadow-md transition-shadow" data-testid={`pricing-testimonial-${i}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold ${t.color}`}>
                    {t.avatar}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white text-sm">{t.name}</p>
                    <p className="text-xs text-slate-400 dark:text-slate-500">{t.role}</p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed italic">"{t.quote}"</p>
                <div className="flex gap-0.5 mt-3">
                  {[1,2,3,4,5].map(s => <Star key={s} className="w-3.5 h-3.5 fill-brand-orange text-brand-orange" />)}
                </div>
              </div>
            ))}
          </div>

          {/* Key figures */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8" data-testid="pricing-key-figures">
            {(language === 'fr' ? [
              { value: '500+', label: 'Apprenants formés', icon: Users },
              { value: '4.8/5', label: 'Satisfaction moyenne', icon: Star },
              { value: '95%', label: 'Taux de réussite', icon: Award },
              { value: '35', label: 'Modules disponibles', icon: BookOpen },
            ] : [
              { value: '500+', label: 'Trained learners', icon: Users },
              { value: '4.8/5', label: 'Average satisfaction', icon: Star },
              { value: '95%', label: 'Success rate', icon: Award },
              { value: '35', label: 'Available modules', icon: BookOpen },
            ]).map((stat, i) => {
              const StatIcon = stat.icon;
              return (
                <div key={i} className="bg-white dark:bg-[#1a1d27] rounded-xl border border-gray-200 dark:border-gray-700 p-5 text-center" data-testid={`pricing-stat-${i}`}>
                  <StatIcon className="w-5 h-5 mx-auto mb-2 text-brand-blue" />
                  <p className="text-2xl font-bold text-brand-dark dark:text-white" style={{ fontFamily: 'Poppins' }}>{stat.value}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Feature Comparison Table */}
        <div className="mt-16 bg-white dark:bg-[#1a1d27] rounded-2xl border border-gray-200 dark:border-gray-700 p-4 sm:p-8" data-testid="comparison-section">
          <h2 className="text-2xl font-bold text-brand-dark dark:text-white text-center mb-2" style={{ fontFamily: 'Poppins' }}>
            {language === 'fr' ? 'Comparaison détaillée des plans' : 'Detailed plan comparison'}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-8">
            {language === 'fr' ? 'Trouvez le plan qui correspond à vos besoins' : 'Find the plan that fits your needs'}
          </p>
          <p className="text-xs text-gray-400 text-center mb-2 sm:hidden">{language === 'fr' ? '← Glissez pour voir tous les plans →' : '← Swipe to see all plans →'}</p>

          <div className="overflow-x-auto -mx-4 px-4 pb-2">
            <table className="w-full text-sm min-w-[700px]" data-testid="comparison-table">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-3 text-gray-500 dark:text-gray-400 font-medium w-[28%]">{language === 'fr' ? 'Fonctionnalité' : 'Feature'}</th>
                  <th className="py-3 px-2 text-center font-bold text-gray-500 dark:text-gray-400 w-[18%]">
                    <Sparkles className="w-4 h-4 mx-auto mb-1 text-gray-400" />
                    Starter
                  </th>
                  <th className="py-3 px-2 text-center font-bold text-brand-blue w-[18%]">
                    <Zap className="w-4 h-4 mx-auto mb-1 text-brand-blue" />
                    Fondations
                  </th>
                  <th className="py-3 px-2 text-center font-bold text-brand-orange w-[18%]">
                    <Crown className="w-4 h-4 mx-auto mb-1 text-brand-orange" />
                    Pro
                  </th>
                  <th className="py-3 px-2 text-center font-bold text-brand-dark dark:text-white w-[18%]">
                    <Building2 className="w-4 h-4 mx-auto mb-1 text-brand-dark" />
                    Expert
                  </th>
                </tr>
              </thead>
              <tbody>
                {(language === 'fr' ? [
                  { f: 'Prix', d: 'Gratuit', e: '19€/mois', p: '49€/mois', ent: '99€/pers/mois' },
                  { f: 'Durée formation', d: '2h (4 modules)', e: '2h+5h (11 modules)', p: '8h (11 modules)', ent: 'Tous (35 modules)' },
                  { f: 'Nombre de quiz', d: '1', e: '4 adaptatifs', p: '7 + mode adaptatif', ent: '9 + vidéos' },
                  { f: 'Assistant IA Claude', d: true, e: false, p: true, ent: true },
                  { f: 'Lexique IA', d: false, e: true, p: true, ent: true },
                  { f: 'Certificats', d: false, e: false, p: true, ent: true },
                  { f: 'Vidéos intégrées', d: false, e: false, p: true, ent: true },
                  { f: 'Badges & Streaks', d: false, e: true, p: true, ent: true },
                  { f: 'Support prioritaire', d: false, e: false, p: true, ent: true },
                  { f: 'Tableau de bord manager', d: false, e: false, p: false, ent: true },
                  { f: 'Modules Stratégie IA', d: false, e: false, p: true, ent: true },
                  { f: 'Validité', d: '7 jours', e: '30 jours', p: '30 jours', ent: '30 jours' },
                ] : [
                  { f: 'Price', d: 'Free', e: '€19/mo', p: '€49/mo', ent: '€99/person/mo' },
                  { f: 'Training duration', d: '2h (4 modules)', e: '2h+5h (11 modules)', p: '8h (11 modules)', ent: 'All (35 modules)' },
                  { f: 'Number of quizzes', d: '1', e: '4 adaptive', p: '7 + adaptive mode', ent: '9 + videos' },
                  { f: 'Claude AI Assistant', d: true, e: false, p: true, ent: true },
                  { f: 'AI Lexicon', d: false, e: true, p: true, ent: true },
                  { f: 'Certificates', d: false, e: false, p: true, ent: true },
                  { f: 'Embedded videos', d: false, e: false, p: true, ent: true },
                  { f: 'Badges & Streaks', d: false, e: true, p: true, ent: true },
                  { f: 'Priority support', d: false, e: false, p: true, ent: true },
                  { f: 'Manager dashboard', d: false, e: false, p: false, ent: true },
                  { f: 'AI Strategy modules', d: false, e: false, p: true, ent: true },
                  { f: 'Validity', d: '7 days', e: '30 days', p: '30 days', ent: '30 days' },
                ]).map((row, i) => (
                  <tr key={i} className={`border-b border-gray-100 dark:border-gray-800 ${i % 2 === 0 ? 'bg-gray-50/50 dark:bg-gray-800/30' : ''}`}>
                    <td className="py-3 px-3 font-medium text-gray-700 dark:text-gray-300 text-xs sm:text-sm">{row.f}</td>
                    {['d', 'e', 'p', 'ent'].map((key) => (
                      <td key={key} className="py-3 px-4 text-center">
                        {row[key] === true ? (
                          <Check className={`w-5 h-5 mx-auto ${key === 'p' ? 'text-brand-orange' : key === 'ent' ? 'text-brand-dark' : key === 'e' ? 'text-brand-blue' : 'text-gray-400'}`} />
                        ) : row[key] === false ? (
                          <X className="w-5 h-5 mx-auto text-gray-200 dark:text-gray-600" />
                        ) : (
                          <span className={`text-xs font-medium ${key === 'p' ? 'text-brand-orange bg-orange-50 dark:bg-orange-900/30 px-2 py-0.5 rounded-full' : key === 'ent' ? 'text-brand-dark dark:text-white bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full' : key === 'e' ? 'text-brand-blue bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-full' : 'text-gray-500 dark:text-gray-400'}`}>{row[key]}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="py-4 px-4 font-medium text-gray-700 dark:text-gray-300">
                    {language === 'fr' ? 'Commencer' : 'Get started'}
                  </td>
                  {['d', 'e', 'p', 'ent'].map((key) => (
                    <td key={key} className="py-4 px-4 text-center">
                      <Button
                        size="sm"
                        className={`text-xs px-4 ${
                          key === 'p' ? 'bg-brand-orange text-white hover:brightness-110' :
                          key === 'ent' ? 'bg-brand-dark text-white hover:bg-gray-800' :
                          key === 'e' ? 'bg-brand-blue text-white hover:brightness-110' :
                          'bg-gray-200 dark:bg-gray-700 text-brand-dark dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600'
                        }`}
                        onClick={() => !user ? navigate('/auth?mode=register') : handleCheckout(key === 'd' ? 'decouverte' : key === 'e' ? 'essentiel' : key === 'p' ? 'pro' : 'entreprise')}
                        data-testid={`comparison-cta-${key}`}
                      >
                        {!user
                          ? (language === 'fr' ? "S'inscrire" : 'Sign up')
                          : key === 'd' ? (language === 'fr' ? 'Gratuit' : 'Free') : (language === 'fr' ? 'Choisir' : 'Choose')
                        }
                      </Button>
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Competitor comparison */}
        <div className="mt-8 bg-white dark:bg-[#1a1d27] rounded-2xl border border-slate-200 dark:border-gray-700 p-4 sm:p-8" data-testid="competitor-section">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white text-center mb-2" style={{ fontFamily: 'Poppins' }}>
            {language === 'fr' ? 'Pourquoi choisir IA Academy ?' : 'Why choose IA Academy?'}
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 text-center mb-8">
            {language === 'fr' ? 'Comparez-nous aux alternatives du marché' : 'Compare us to market alternatives'}
          </p>
          <p className="text-xs text-gray-400 text-center mb-2 sm:hidden">{language === 'fr' ? '← Glissez pour comparer →' : '← Swipe to compare →'}</p>

          <div className="overflow-x-auto -mx-4 px-4 pb-2">
            <table className="w-full text-sm min-w-[620px]" data-testid="competitor-table">
              <thead>
                <tr className="border-b border-slate-200 dark:border-gray-700">
                  <th className="text-left py-3 px-3 text-slate-500 dark:text-slate-400 font-medium w-[22%]">{language === 'fr' ? 'Critère' : 'Criteria'}</th>
                  <th className="py-3 px-2 text-center w-[22%]">
                    <div className="flex items-center justify-center gap-1">
                      <img src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png" alt="" className="w-4 h-4 rounded" />
                      <span className="font-bold text-brand-blue text-xs sm:text-sm">IA Academy</span>
                    </div>
                  </th>
                  <th className="py-3 px-2 text-center font-medium text-slate-500 dark:text-slate-400 text-xs sm:text-sm w-[18%]">OpenClassrooms</th>
                  <th className="py-3 px-2 text-center font-medium text-slate-500 dark:text-slate-400 text-xs sm:text-sm w-[18%]">Jedha</th>
                  <th className="py-3 px-2 text-center font-medium text-slate-500 dark:text-slate-400 text-xs sm:text-sm w-[20%]">Coursera</th>
                </tr>
              </thead>
              <tbody>
                {(language === 'fr' ? [
                  { criteria: 'Prix', ia: '0-49€/mois', oc: '20€/mois', jedha: '1 500-7 000€', coursera: '39-59€/mois' },
                  { criteria: 'Focus 100% IA', ia: true, oc: false, jedha: true, coursera: false },
                  { criteria: 'Assistant IA intégré', ia: true, oc: false, jedha: false, coursera: false },
                  { criteria: 'Bilingue FR/EN', ia: true, oc: true, jedha: true, coursera: false },
                  { criteria: 'Quiz adaptatifs', ia: '9 quiz', oc: 'Variable', jedha: 'Limité', coursera: 'Variable' },
                  { criteria: 'Certificats', ia: true, oc: true, jedha: true, coursera: true },
                  { criteria: 'Offre entreprise', ia: true, oc: true, jedha: true, coursera: true },
                  { criteria: 'Format flexible', ia: true, oc: false, jedha: false, coursera: false },
                ] : [
                  { criteria: 'Price', ia: '€0-49/mo', oc: '€20/mo', jedha: '€1,500-7,000', coursera: '$39-59/mo' },
                  { criteria: '100% AI Focus', ia: true, oc: false, jedha: true, coursera: false },
                  { criteria: 'Built-in AI Assistant', ia: true, oc: false, jedha: false, coursera: false },
                  { criteria: 'Bilingual FR/EN', ia: true, oc: true, jedha: true, coursera: false },
                  { criteria: 'Adaptive quizzes', ia: '9 quizzes', oc: 'Variable', jedha: 'Limited', coursera: 'Variable' },
                  { criteria: 'Certificates', ia: true, oc: true, jedha: true, coursera: true },
                  { criteria: 'Enterprise offer', ia: true, oc: true, jedha: true, coursera: true },
                  { criteria: 'Flexible format', ia: true, oc: false, jedha: false, coursera: false },
                ]).map((row, i) => (
                  <tr key={i} className={`border-b border-slate-100 dark:border-gray-800 ${i % 2 === 0 ? 'bg-slate-50/50 dark:bg-gray-800/30' : ''}`}>
                    <td className="py-3 px-3 font-medium text-slate-700 dark:text-slate-300 text-xs sm:text-sm">{row.criteria}</td>
                    {['ia', 'oc', 'jedha', 'coursera'].map((key) => (
                      <td key={key} className="py-3 px-4 text-center">
                        {row[key] === true ? (
                          <Check className={`w-5 h-5 mx-auto ${key === 'ia' ? 'text-brand-blue' : 'text-slate-400'}`} />
                        ) : row[key] === false ? (
                          <X className="w-5 h-5 mx-auto text-gray-300 dark:text-gray-600" />
                        ) : (
                          <span className={`text-xs font-medium ${key === 'ia' ? 'text-brand-blue bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-full' : 'text-slate-500 dark:text-slate-400'}`}>{row[key]}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Score */}
          <div className="mt-8 text-center p-6 bg-gradient-to-r from-blue-50 to-orange-50 dark:from-blue-900/20 dark:to-orange-900/20 rounded-xl" data-testid="score-section">
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">{language === 'fr' ? 'Score compétitif IA Academy' : 'IA Academy Competitive Score'}</p>
            <div className="flex items-center justify-center gap-3">
              <span className="text-5xl font-bold text-brand-blue" style={{ fontFamily: 'Poppins' }}>8.5</span>
              <span className="text-2xl text-slate-400 dark:text-slate-500">/10</span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 max-w-md mx-auto">
              {language === 'fr'
                ? 'Meilleur rapport qualité-prix du marché. 100% IA, bilingue, avec assistant intelligent intégré — une combinaison unique.'
                : 'Best value for money on the market. 100% AI, bilingual, with built-in smart assistant — a unique combination.'}
            </p>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-16 max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white text-center mb-8" style={{ fontFamily: 'Poppins' }}>
            {language === 'fr' ? 'Questions fréquentes' : 'Frequently Asked Questions'}
          </h2>
          <div className="space-y-4">
            {(language === 'fr' ? [
              { q: "Comment fonctionne le plan IA Starter ?", a: "Le plan IA Starter est entièrement gratuit. Il vous offre 1 heure de formation express (outils IA, écrire un prompt, exercice pratique), 1 quiz et l'accès à l'assistant IA pendant 7 jours. Idéal pour tester la plateforme !" },
              { q: "Quelle est la différence entre IA Fondations et IA Pro ?", a: "Le plan IA Fondations (19€/mois) donne accès aux programmes de 2h et 5h avec quiz adaptatifs, lexique IA et progression sauvegardée. IA Pro (49€/mois) ajoute tous les programmes (dont le 8h), l'assistant IA Claude illimité, les certificats téléchargeables, les vidéos et le support prioritaire." },
              { q: "Que comprend le plan IA Expert ?", a: "Le plan IA Expert (99€/personne/mois) inclut 13 modules exclusifs : stratégie IA et ROI, AI Act et gouvernance des données, agents IA autonomes, conduite du changement, outils CRM et analyse de données. Plus un tableau de bord manager, des certificats personnalisés et un support dédié. Contact : contact@ia-academy.ai" },
              { q: "Puis-je changer de plan ?", a: "Oui, vous pouvez passer au plan supérieur à tout moment. Votre progression est toujours conservée." },
              { q: "Les abonnements sont-ils renouvelés automatiquement ?", a: "Non. Chaque abonnement est valable 30 jours. Vous pouvez le renouveler manuellement à la fin de la période." },
              { q: "Puis-je obtenir un remboursement ?", a: "Oui, vous disposez de 7 jours pour demander un remboursement si la formation ne correspond pas à vos attentes." },
            ] : [
              { q: "How does the IA Starter plan work?", a: "The IA Starter plan is completely free. It offers 1 hour of express training (AI tools, writing prompts, practical exercise), 1 quiz and AI assistant access for 7 days. Perfect for testing the platform!" },
              { q: "What's the difference between IA Fondations and IA Pro?", a: "IA Fondations (€19/mo) gives access to 2h and 5h programs with adaptive quizzes, AI lexicon and saved progress. IA Pro (€49/mo) adds all programs (including 8h), unlimited Claude AI assistant, downloadable certificates, videos and priority support." },
              { q: "What does the IA Expert plan include?", a: "IA Expert (€99/person/mo) includes 13 exclusive modules: AI strategy and ROI, AI Act and data governance, autonomous AI agents, change management, CRM tools and data analysis. Plus a manager dashboard, custom certificates and dedicated priority support. Contact: contact@ia-academy.ai" },
              { q: "Can I change plans?", a: "Yes, you can upgrade at any time. Your progress is always preserved." },
              { q: "Are subscriptions auto-renewed?", a: "No. Each subscription is valid for 30 days. You can renew manually at the end of the period." },
              { q: "Can I get a refund?", a: "Yes, you have 7 days to request a refund if the training doesn't meet your expectations." },
            ]).map((faq, i) => (
              <div key={i} className="bg-white dark:bg-[#1a1d27] rounded-xl border border-slate-200 dark:border-gray-700 p-5" data-testid={`faq-${i}`}>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">{faq.q}</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Enterprise Contact Form Modal */}
      {showEnterpriseForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="enterprise-modal">
          <div className="bg-white dark:bg-[#1a1d27] rounded-2xl w-full max-w-lg p-8 relative animate-fade-in-up shadow-2xl">
            <button
              onClick={() => { setShowEnterpriseForm(false); setFormSent(false); setFormData({ name: '', email: '', company: '', employees_count: '', message: '' }); }}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-white"
              data-testid="close-enterprise-modal"
            >
              <X className="w-5 h-5" />
            </button>

            {formSent ? (
              <div className="text-center py-8" data-testid="enterprise-form-success">
                <div className="w-16 h-16 bg-blue-50 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <Check className="w-8 h-8 text-brand-blue" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2" style={{ fontFamily: 'Poppins' }}>
                  {language === 'fr' ? 'Demande envoyée !' : 'Request sent!'}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm">
                  {language === 'fr'
                    ? 'Nous vous contacterons sous 24h pour discuter de votre formation sur mesure.'
                    : 'We will contact you within 24 hours to discuss your custom training.'}
                </p>
                <Button className="mt-6 bg-brand-blue text-white hover:brightness-110" onClick={() => { setShowEnterpriseForm(false); setFormSent(false); }}>
                  {language === 'fr' ? 'Fermer' : 'Close'}
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#0f172a] to-[#1a1f3a] rounded-xl flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-brand-orange" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Poppins' }}>
                      {language === 'fr' ? 'Offre IA Expert' : 'IA Expert Offer'}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {language === 'fr' ? 'Formez vos équipes à l\'IA' : 'Train your teams in AI'}
                    </p>
                  </div>
                </div>

                <form onSubmit={handleEnterpriseSubmit} className="space-y-4" data-testid="enterprise-form">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      {language === 'fr' ? 'Nom complet' : 'Full name'} *
                    </label>
                    <input
                      type="text" required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg border border-slate-200 dark:border-gray-700 dark:bg-[#0f1117] dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      placeholder={language === 'fr' ? 'Jean Dupont' : 'John Doe'}
                      data-testid="enterprise-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email *</label>
                    <input
                      type="email" required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg border border-slate-200 dark:border-gray-700 dark:bg-[#0f1117] dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      placeholder="jean@entreprise.com"
                      data-testid="enterprise-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      {language === 'fr' ? 'Nom de l\'entreprise' : 'Company name'} *
                    </label>
                    <input
                      type="text" required
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg border border-slate-200 dark:border-gray-700 dark:bg-[#0f1117] dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      placeholder={language === 'fr' ? 'Ma Société SAS' : 'My Company LLC'}
                      data-testid="enterprise-company-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      <Users className="w-3.5 h-3.5 inline mr-1" />
                      {language === 'fr' ? 'Nombre de collaborateurs à former' : 'Number of employees to train'} *
                    </label>
                    <select
                      required
                      value={formData.employees_count}
                      onChange={(e) => setFormData({ ...formData, employees_count: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg border border-slate-200 dark:border-gray-700 dark:bg-[#0f1117] dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      data-testid="enterprise-employees-select"
                    >
                      <option value="">{language === 'fr' ? 'Sélectionnez...' : 'Select...'}</option>
                      <option value="1-5">1-5</option>
                      <option value="6-20">6-20</option>
                      <option value="21-50">21-50</option>
                      <option value="51-100">51-100</option>
                      <option value="100+">100+</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      {language === 'fr' ? 'Message (optionnel)' : 'Message (optional)'}
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg border border-slate-200 dark:border-gray-700 dark:bg-[#0f1117] dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
                      rows={3}
                      placeholder={language === 'fr' ? 'Décrivez vos besoins de formation...' : 'Describe your training needs...'}
                      data-testid="enterprise-message-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-brand-orange text-white hover:brightness-110 active:scale-[0.98] transition-all font-semibold shadow-md"
                    disabled={formLoading}
                    data-testid="enterprise-submit-btn"
                  >
                    {formLoading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        {language === 'fr' ? 'Envoyer la demande' : 'Send request'}
                      </>
                    )}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
